import { Badge, Box, RadioGroup, Table } from '@palmetto/palmetto-components';
import { useMemo } from 'react';

import { PaymentPlan, PayoutEvent, PayoutEventMap, ProgramType } from 'types';

import './ScrollablePaymentPlanTable.css';

interface ScrollablePaymentPlanTableProps {
  paymentPlans: Omit<PaymentPlan, 'id'>[];
  programType: ProgramType;
  currentPaymentPlanId?: string;
  selectedPaymentPlanId?: string;
  selectPaymentPlan?(id: string): void;
  showFirstColumn?: boolean;
}

const getPaymentScheduleColumns = (
  programType: ProgramType,
  currentPaymentPlanId: string | undefined,
  selectedPaymentPlanId: string | undefined,
  selectPaymentPlan?: (id: string) => void,
) => {
  const baseColumns = [
    {
      heading: '',
      dataKey: 'name',
      render: (cell?: any, _row?: any) => (
        <Box fontWeight="medium" as="span" display="inline">
          {cell}
        </Box>
      ),
    },
    {
      heading: '',
      dataKey: 'currentPlan',
      render: (_cell?: any, row?: any) => {
        return row.id === currentPaymentPlanId ? <Badge message="Current" size="sm" variant="secondary" /> : null;
      },
    },
  ];
  const dynamicColumns = PayoutEventMap[programType]
    .filter((entry) => ![PayoutEvent.other, PayoutEvent.estimate, PayoutEvent.invoice].includes(entry.event))
    .toSorted((a, b) => a.order - b.order)
    .map((entry) => ({
      heading: entry?.name,
      dataKey: entry.event,
      render: (_cell?: any, row?: any, _rowIndex?: number) => {
        const valueEntry = row.paymentSchedule?.find((event: any) => event.event === entry.event);
        return (
          <Box direction="row" alignItems="center" childGap="xs">
            <Box fontWeight="medium" as="span" display="inline">
              {valueEntry?.paymentPercent ? `${valueEntry.paymentPercent * 100}%` : '-'}
            </Box>
            {valueEntry?.paymentPercent && valueEntry.pausedDays ? (
              <Badge message={`${valueEntry.pausedDays}d hold`} size="sm" variant="primary" />
            ) : null}
          </Box>
        );
      },
    }));

  if (selectPaymentPlan) {
    baseColumns.unshift({
      heading: 'Plan Name',
      dataKey: 'id',
      render: (_cell?: any, row?: any) => {
        return row ? (
          <RadioGroup
            value={selectedPaymentPlanId}
            name="selectedPaymentPlan"
            onChange={() => selectPaymentPlan(row.id)}
            options={[{ id: row.id, value: row.id, label: '' }]}
          />
        ) : (
          <></>
        );
      },
    });
  }

  return [...baseColumns, ...dynamicColumns];
};

export const ScrollablePaymentPlanTable = ({
  paymentPlans,
  programType,
  currentPaymentPlanId,
  selectedPaymentPlanId,
  selectPaymentPlan,
}: ScrollablePaymentPlanTableProps) => {
  const columns = useMemo(
    () => getPaymentScheduleColumns(programType, currentPaymentPlanId, selectedPaymentPlanId, selectPaymentPlan),
    [programType, currentPaymentPlanId, selectedPaymentPlanId, selectPaymentPlan],
  );
  return paymentPlans ? (
    <Box childGap="md">
      <Box style={{ fontWeight: '575' }}>
        Select the payment plan for the organization. Any changes will be applied to new projects as of the effective
        date.
      </Box>
      <Box style={{ height: '325px', width: '100%' }}>
        <Table
          className="tableBackgroundColor"
          hasStickyHeader
          rowKey="id"
          isScrollable={{ x: false, y: true }}
          columns={columns}
          rows={paymentPlans}
        />
      </Box>
    </Box>
  ) : null;
};
